// This file is not yet translated.
/* eslint-disable i18next/no-literal-string */
import {
  Box,
  Button,
  Divider,
  Image,
  ModalCloseButton,
  Typography,
  styled,
} from "@outschool/backpack";
import { Activity } from "@outschool/gql-backend-generated";
import { transformedImageUrl } from "@outschool/media-urls";
import { NudgeModal } from "@outschool/ui-components-shared";
import { useDevicePixelRatio, useDeviceTypeContext } from "@outschool/ui-utils";
import React, { useEffect } from "react";

import SmileyInput from "./SmileyInput";

const CLASS_IMAGE_SIZE = 72;
const TEACHER_IMAGE_SIZE = 32;

const SX_TRUNCATE_TWO_LINES = {
  display: "-webkit-box",
  overflow: "hidden",
  textOverflow: "ellipsis",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
};

const StyledCloseButton = styled(ModalCloseButton)({
  position: "absolute",
  top: 8,
  right: 8,
  fontSize: "2.4rem",
});

interface PreActivityReviewModalActivity {
  uid: string;
  title: string;
  titleTranslated?: string | null;
  details: Pick<Activity["details"], "photo">;
  leader: Pick<Activity["leader"], "name" | "photo">;
}

interface ReviewModalProps {
  activity: PreActivityReviewModalActivity;
  learnerName: string;
  onJoin: (rating: number) => void;
  onSelectRating?: (rating: number) => void;
  onMount?: () => void;
  onClose: () => void;
}

const PreActivityReviewModal = ({
  activity,
  learnerName,
  onJoin,
  onSelectRating,
  onMount,
  onClose,
}: ReviewModalProps) => {
  const [rating, setRating] = React.useState<number>(0);
  const { deviceType } = useDeviceTypeContext();
  const pixelRatio = useDevicePixelRatio(deviceType);

  useEffect(() => {
    onMount?.();
  }, [onMount]);

  const classPhotoUrl = transformedImageUrl(activity.details.photo, {
    height: CLASS_IMAGE_SIZE * pixelRatio,
    width: CLASS_IMAGE_SIZE * pixelRatio,
    fit: "crop",
  });
  const teacherPhotoUrl = transformedImageUrl(activity.leader.photo, {
    height: TEACHER_IMAGE_SIZE * pixelRatio,
    width: TEACHER_IMAGE_SIZE * pixelRatio,
    fit: "crop",
  });

  const handleSetRating = (rating: number) => {
    setRating(rating);
    onSelectRating?.(rating);
  };

  return (
    <NudgeModal open onClose={onClose}>
      <StyledCloseButton onClick={onClose} aria-label="close" />
      <Typography variant="h5" emphasized>
        {`How excited are you for class today, ${learnerName}?`}
      </Typography>
      <Divider sx={{ my: 12 }} />
      <Box flex sx={{ gap: 16 }}>
        <Image
          sx={{
            width: CLASS_IMAGE_SIZE,
            height: CLASS_IMAGE_SIZE,
            borderRadius: 8,
            objectFit: "cover",
          }}
          src={classPhotoUrl}
          height={CLASS_IMAGE_SIZE}
          width={CLASS_IMAGE_SIZE}
          alt="Class listing photo"
        />
        <Box>
          <Typography
            component="div"
            sx={{
              fontWeight: "500",
              mb: "0.5rem",
              ...SX_TRUNCATE_TWO_LINES,
            }}
          >
            {activity.titleTranslated || activity.title}
          </Typography>
          <Box flex sx={{ alignItems: "center", gap: 12 }}>
            <Image
              sx={{
                width: TEACHER_IMAGE_SIZE,
                height: TEACHER_IMAGE_SIZE,
                borderRadius: 99, // round
                objectFit: "cover",
              }}
              src={teacherPhotoUrl}
              height={TEACHER_IMAGE_SIZE}
              width={TEACHER_IMAGE_SIZE}
              alt="Teacher photo"
            />
            <Typography
              sx={{
                fontWeight: "500",
                color: "grey.600",
                lineHeight: "1.2",
                ...SX_TRUNCATE_TWO_LINES,
              }}
            >
              {activity.leader.name}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Divider sx={{ my: 12 }} />
      <Box flex sx={{ justifyContent: "center", mb: 8 }}>
        <SmileyInput rating={rating} setRating={handleSetRating} />
      </Box>
      <Box flex sx={{ justifyContent: "center", mb: 16 }}>
        <Button
          variant="contained"
          fullWidth
          onClick={() => onJoin(rating)}
          sx={{ px: "6rem" }}
        >
          {"Join Class"}
        </Button>
      </Box>
    </NudgeModal>
  );
};

export default PreActivityReviewModal;
